
import { defineComponent, PropType } from "vue";
import DraftTreeSubtree from "./DraftTreeSubtree.vue";
import { collapsibleUsernames, DraftForest, DraftTreeMode } from "./draftTree";

/** Tamaños de letra del lienzo; todo lo de dentro va en `em` y escala con él. */
const ZOOMS = [8, 10, 12, 14];

/**
 * UN ÁRBOL DEL DRAFT, DIBUJADO — el binario, el de patrocinio o el del CMMI.
 *
 * RECIBE EL BOSQUE YA CONSTRUIDO, no el draft: los tres árboles salen de drafts
 * distintos (el del paso de los árboles y el del CMMI, que es otro paso y otra
 * revisión), y hacer que este componente supiera construir los tres lo habría
 * atado a los dos formatos. Quien lo usa arma el bosque con `buildDraftForest` o
 * `buildMmiForest` y le pasa el resultado; aquí sólo se pinta.
 *
 * Pinta UN árbol porque las pestañas viven arriba, en el componente de revisión:
 * así las instancias sobreviven al cambio de pestaña (`v-show`, no `v-if`) y
 * cada una conserva su scroll, que es lo que permite volver al mismo punto del
 * otro árbol para compararlos.
 *
 * NO LLAMA AL BACKEND. El draft ya está en memoria en la consola; esto sólo lo
 * mira. Y NO reusa `OrgTreeNode` (la Vista de Árbol de siempre) porque aquél
 * pinta lo que YA está en Firestore, pidiéndole los hijos al backend nodo a
 * nodo: aquí no hay nada escrito todavía — de eso se trata.
 */
export default defineComponent({
  name: "DraftTreeView",
  components: { DraftTreeSubtree },
  props: {
    forest: { type: Object as PropType<DraftForest>, required: true },
  },
  data() {
    return {
      zoom: 1,
      collapsed: new Set<string>(),
    };
  },
  computed: {
    mode(): DraftTreeMode {
      return this.forest.mode;
    },
  },
  methods: {
    zoomBy(step: number): void {
      this.zoom = Math.min(ZOOMS.length - 1, Math.max(0, this.zoom + step));
    },
    expandAll(): void {
      this.collapsed = new Set<string>();
    },
    collapseAll(): void {
      this.collapsed = new Set<string>(collapsibleUsernames(this.forest));
    },
  },
  setup() {
    // Constante de módulo, no estado: no tiene por qué ser reactiva.
    return { ZOOMS };
  },
  provide() {
    // El plegado vive aquí (un solo «expandir todo») y baja por inject: un emit
    // tendría que reenviarse nivel a nivel hasta la raíz del árbol.
    return {
      draftTreeCollapse: {
        isCollapsed: (username: string): boolean =>
          this.collapsed.has(username),
        toggle: (username: string): void => {
          const next = new Set(this.collapsed);
          if (!next.delete(username)) next.add(username);
          this.collapsed = next;
        },
      },
    };
  },
});
